import { Checkbox, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { BulkChangeRow, BulkFieldChange } from 'helpers/bulkEdit/changePlan';
import React from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

/** Live state of a record once a run has started. */
export type RowRunStatus = 'pending' | 'writing' | 'retrying' | 'done' | 'failed';

const STATUS_LABELS: { [key in RowRunStatus]: string } = {
	pending: 'Waiting',
	writing: 'Saving…',
	retrying: 'Retrying…',
	done: 'Updated',
	failed: 'Failed',
};

const ROW_HEIGHT = 44;
const LINE_HEIGHT = 22;
const MAX_LIST_HEIGHT = 360;
/**
 * Roughly how much of a change fits on one line at the column's width. Used only to reserve
 * height; the text itself wraps naturally, and the tooltip always carries the full value.
 */
const CHARS_PER_LINE = 58;
const MAX_LINES_PER_CHANGE = 3;

function changeLineCount(change: BulkFieldChange) {
	let length = `${change.label}: ${change.currentLabel} → ${change.newLabel}`.length;
	return Math.min(Math.max(Math.ceil(length / CHARS_PER_LINE), 1), MAX_LINES_PER_CHANGE);
}

/** A row grows a line at a time when a record changes several fields at once. */
function rowHeight(row: BulkChangeRow) {
	if (row.status !== 'change' || !row.changes?.length) {
		return ROW_HEIGHT;
	}

	let lines = row.changes.reduce((total, change) => total + changeLineCount(change), 0);
	return Math.max(ROW_HEIGHT, lines * LINE_HEIGHT + LINE_HEIGHT);
}

/**
 * True when the two values look the same on screen but are not, which happens when the only
 * difference is spacing. Without a hint the row would read as a change to nothing.
 */
function differsOnlyBySpacing(change: BulkFieldChange) {
	let collapse = (value: string) => (value ?? '').replace(/\s+/g, ' ').trim();
	return change.currentLabel !== change.newLabel && collapse(change.currentLabel) === collapse(change.newLabel);
}

const useStyles = makeStyles((theme) => ({
	header: {
		display: 'flex',
		alignItems: 'center',
		fontWeight: 700,
		borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
		paddingRight: 16,
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
		paddingRight: 16,
	},
	changed: {
		backgroundColor: 'rgba(255, 193, 7, 0.12)',
	},
	doneRow: {
		backgroundColor: 'rgba(76, 175, 80, 0.12)',
	},
	failedRow: {
		backgroundColor: 'rgba(244, 67, 54, 0.12)',
	},
	skipped: {
		color: theme.palette.text.secondary,
	},
	scroller: {
		// Narrow screens scroll the columns rather than squeezing them into unreadable slivers.
		overflowX: 'auto',
	},
	grid: {
		minWidth: 980,
	},
	cell: {
		padding: '0 8px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	checkboxCell: {
		width: 56,
		flex: '0 0 auto',
		padding: '0 2px',
		overflow: 'visible',
	},
	cardCell: { width: 90, flex: '0 0 auto' },
	nameCell: { flex: '1 1 0', minWidth: 110 },
	refCell: { flex: '1.2 1 0', minWidth: 130 },
	dateCell: { width: 110, flex: '0 0 auto' },
	changeCell: {
		flex: '2.4 1 0',
		minWidth: 370,
		padding: '0 8px',
		// Multi field changes stack, so this cell wraps instead of clipping.
		whiteSpace: 'normal',
		overflow: 'hidden',
	},
	changeLine: {
		lineHeight: `${LINE_HEIGHT}px`,
		// Wraps rather than clipping, so a long value stays readable.
		whiteSpace: 'normal',
		overflowWrap: 'anywhere',
	},
	spacingHint: {
		marginLeft: 6,
		fontStyle: 'italic',
		fontWeight: 400,
		color: theme.palette.text.secondary,
	},
	statusCell: {
		width: 92,
		flex: '0 0 auto',
		padding: '0 8px',
		fontWeight: 700,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	statusDone: { color: '#2e7d32' },
	statusFailed: { color: '#c62828' },
	statusBusy: { color: theme.palette.primary.main },
	statusIdle: { color: theme.palette.text.secondary },
	arrow: {
		margin: '0 6px',
		color: theme.palette.text.secondary,
	},
	fieldName: {
		marginRight: 4,
		color: theme.palette.text.secondary,
	},
	newValue: {
		fontWeight: 700,
	},
}));

interface Props {
	rows: BulkChangeRow[];
	excluded: { [id: string]: boolean };
	onToggle: (id: string) => void;
	/** Once a run starts the checkboxes give way to live per record status. */
	statuses?: { [id: string]: RowRunStatus };
	errors?: { [id: string]: string };
}

export function PreviewTable({ rows, excluded, onToggle, statuses, errors }: Props) {
	let classes = useStyles();
	let running = !!statuses;
	let listRef = React.useRef<VariableSizeList>(null);

	// Row heights are cached by index, so they have to be dropped when the rows change.
	React.useEffect(() => {
		listRef.current?.resetAfterIndex(0, true);
	}, [rows]);

	let totalHeight = React.useMemo(() => rows.reduce((total, row) => total + rowHeight(row), 0), [rows]);

	function changeTooltip(row: BulkChangeRow) {
		if (row.status !== 'change') {
			return row.reason;
		}

		if (row.changes?.length) {
			return row.changes.map((change) => `${change.label}: ${change.currentLabel} → ${change.newLabel}`).join('\n');
		}

		return `${row.currentLabel} → ${row.newLabel}`;
	}

	function statusClass(status?: RowRunStatus) {
		if (status === 'done') return classes.statusDone;
		if (status === 'failed') return classes.statusFailed;
		if (status === 'writing' || status === 'retrying') return classes.statusBusy;
		return classes.statusIdle;
	}

	const Row = ({ index, style }: ListChildComponentProps) => {
		let row = rows[index];
		let record = row.record ?? {};
		let changeable = row.status === 'change';
		let status = statuses?.[row.id];
		let included = changeable && !excluded[row.id];

		let rowClass = classes.skipped;
		if (status === 'done') {
			rowClass = classes.doneRow;
		} else if (status === 'failed') {
			rowClass = classes.failedRow;
		} else if (changeable && (!running || included)) {
			rowClass = classes.changed;
		}

		return (
			<div style={style} className={`${classes.row} ${rowClass}`}>
				{running ? (
					<div
						className={`${classes.statusCell} ${statusClass(status)}`}
						title={status === 'failed' ? errors?.[row.id] : undefined}
					>
						{status ? STATUS_LABELS[status] : included ? '' : 'Not included'}
					</div>
				) : (
					<div className={`${classes.cell} ${classes.checkboxCell}`}>
						<Checkbox
							size="small"
							color="primary"
							disabled={!changeable}
							checked={included}
							onChange={() => onToggle(row.id)}
							inputProps={{ 'aria-label': `Include record ${record.card_no ?? row.id}` }}
						/>
					</div>
				)}
				<div className={`${classes.cell} ${classes.cardCell}`}>{record.card_no ?? '—'}</div>
				<div className={`${classes.cell} ${classes.nameCell}`} title={record.name}>
					{record.name ?? '—'}
				</div>
				<div className={`${classes.cell} ${classes.refCell}`} title={record.reference}>
					{record.reference ?? '—'}
				</div>
				<div className={`${classes.cell} ${classes.dateCell}`}>{record.permission_date ?? '—'}</div>
				<div className={`${classes.cell} ${classes.changeCell}`} title={changeTooltip(row)}>
					{status === 'failed' && errors?.[row.id] ? (
						<span>{errors[row.id]}</span>
					) : changeable && row.changes?.length ? (
						row.changes.map((change) => (
							<div key={change.field} className={classes.changeLine}>
								<span className={classes.fieldName}>{change.label}:</span>
								<span>{change.currentLabel}</span>
								<span className={classes.arrow}>→</span>
								<span className={classes.newValue}>{change.newLabel}</span>
								{differsOnlyBySpacing(change) && <span className={classes.spacingHint}>spacing only</span>}
							</div>
						))
					) : changeable ? (
						<div className={classes.changeLine}>
							<span>{row.currentLabel}</span>
							<span className={classes.arrow}>→</span>
							<span className={classes.newValue}>{row.newLabel}</span>
						</div>
					) : (
						<div className={classes.changeLine}>
							{row.status === 'unchanged' ? row.reason ?? 'Already correct' : row.reason ?? 'Cannot change'}
						</div>
					)}
				</div>
			</div>
		);
	};

	if (!rows.length) {
		return (
			<Typography variant="body2" color="textSecondary" style={{ padding: 16 }}>
				No records match the current filters.
			</Typography>
		);
	}

	return (
		<div className={classes.scroller}>
			<div className={classes.grid}>
				<div className={classes.header} style={{ height: ROW_HEIGHT }}>
					{running ? (
						<div className={classes.statusCell}>Status</div>
					) : (
						<div className={`${classes.cell} ${classes.checkboxCell}`} />
					)}
					<div className={`${classes.cell} ${classes.cardCell}`}>Card No</div>
					<div className={`${classes.cell} ${classes.nameCell}`}>Name</div>
					<div className={`${classes.cell} ${classes.refCell}`}>Ref</div>
					<div className={`${classes.cell} ${classes.dateCell}`}>Date</div>
					<div className={`${classes.cell} ${classes.changeCell}`}>Change</div>
				</div>
				<VariableSizeList
					ref={listRef}
					height={Math.min(totalHeight, MAX_LIST_HEIGHT)}
					itemCount={rows.length}
					itemSize={(index) => rowHeight(rows[index])}
					estimatedItemSize={ROW_HEIGHT}
					width="100%"
				>
					{Row}
				</VariableSizeList>
			</div>
		</div>
	);
}

export default PreviewTable;
